import React, { useContext } from 'react';
import AppContext from 'context/Context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCheck, faSpinner, faPhone, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

const AssignClientDropdown = ({
  show,
  search,
  onSearchChange,
  clients,
  isLoading,
  isLoadingMore,
  selectedClientId,
  ticketPhone,
  onScroll,
  onSelectClient,
  onOpenModal,
  onClose,
  scrollRef
}) => {
  const { config: { isDark: contextIsDark } = {} } = useContext(AppContext);
  const isDark = typeof contextIsDark === 'boolean'
    ? contextIsDark
    : (typeof document !== 'undefined' && (document.documentElement.classList.contains('dark') || document.body.classList.contains('dark')));

  if (!show) return null;

  return (
    <div
      className="shadow-lg p-2 position-absolute text-start"
      style={{
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1050,
        width: '295px',
        maxWidth: 'calc(100% - 10px)',
        backgroundColor: isDark ? '#121e2d' : '#ffffff',
        border: isDark ? '1px solid #233142' : '1px solid #e2e8f0',
        borderRadius: '8px',
        marginTop: '8px',
        boxShadow: isDark
          ? '0 10px 25px -5px rgba(0, 0, 0, 0.6), 0 8px 10px -6px rgba(0, 0, 0, 0.5)'
          : '0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div
        className="d-flex align-items-center justify-content-between pb-2 mb-2"
        style={{ borderBottom: isDark ? '1px solid #233142' : '1px solid #e2e8f0' }}
      >
        <span
          className="fw-bold text-truncate me-2"
          style={{
            fontSize: '0.78rem',
            color: isDark ? '#f9fafd' : '#0f172a'
          }}
        >
          {ticketPhone ? `Coincidencias (${ticketPhone})` : 'Seleccionar Cliente'}
        </span>
        <button
          type="button"
          className={`btn-close ${isDark ? 'btn-close-white' : ''}`}
          style={{ fontSize: '0.55rem' }}
          onClick={onClose}
        />
      </div>

      {/* Buscador */}
      <div className="mb-2">
        <div className="input-group input-group-sm">
          <input
            type="text"
            className="form-control form-control-sm shadow-none"
            placeholder="Buscar por teléfono, nombre o licencia..."
            style={{
              fontSize: '0.75rem',
              backgroundColor: isDark ? '#182635' : '#ffffff',
              borderColor: isDark ? '#233142' : '#d8e2ef',
              color: isDark ? '#f9fafd' : '#0f172a'
            }}
            value={search}
            onChange={e => onSearchChange(e.target.value)}
          />
          <button
            className="btn btn-outline-primary btn-sm"
            type="button"
            title="Búsqueda en lista completa"
            onClick={onOpenModal}
          >
            <FontAwesomeIcon icon={faSearch} />
          </button>
        </div>
      </div>

      {/* Lista con scroll infinito */}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="overflow-y-auto pe-1"
        style={{ maxHeight: '220px', overflowY: 'auto' }}
      >
        {isLoading ? (
          <div className="text-center py-3" style={{ fontSize: '0.75rem', color: isDark ? '#94a3b8' : '#64748b' }}>
            <FontAwesomeIcon icon={faSpinner} spin className="me-1 text-success" />
            Buscando clientes...
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-3" style={{ fontSize: '0.75rem', color: isDark ? '#94a3b8' : '#64748b' }}>
            <div>No se encontraron coincidencias.</div>
            <button
              type="button"
              className="btn btn-link btn-xs p-0 mt-1 text-primary"
              style={{ fontSize: '0.72rem' }}
              onClick={onOpenModal}
            >
              <FontAwesomeIcon icon={faSearch} className="me-1" />
              Buscar en todos los clientes
            </button>
          </div>
        ) : (
          <>
            {clients.map(c => {
              const isSelected = selectedClientId === c.idClienteAkasia;
              const cardBg = isSelected
                ? (isDark ? 'rgba(16, 185, 129, 0.2)' : '#ecfdf5')
                : (isDark ? '#182635' : '#f8fafc');
              const cardBorder = isSelected
                ? '#10b981'
                : (isDark ? '#233142' : '#e2e8f0');
              const hoverBg = isDark ? '#1e3347' : '#eef2f6';

              return (
                <div
                  key={c.idClienteAkasia}
                  className="p-2 rounded mb-1 cursor-pointer border"
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    backgroundColor: cardBg,
                    borderColor: cardBorder
                  }}
                  onMouseEnter={e => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = hoverBg;
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = cardBg;
                  }}
                  onClick={() => onSelectClient(c)}
                  title="Clic en la tarjeta para asignar a este ticket"
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div style={{ minWidth: 0, flex: 1 }} className="me-2">
                      <div
                        className="fw-bold text-truncate"
                        style={{
                          fontSize: '0.78rem',
                          color: isDark ? '#f9fafd' : '#0f172a'
                        }}
                      >
                        #{c.idClienteAkasia} - {c.vNombreCompleto || 'Sin Nombre'}
                      </div>
                      <div
                        className="fw-medium d-flex align-items-center justify-content-between gap-1 mt-1"
                        style={{
                          fontSize: '0.70rem',
                          color: isDark ? '#94a3b8' : '#64748b',
                          minWidth: 0
                        }}
                      >
                        <span className="text-truncate" style={{ minWidth: 0 }}>
                          <FontAwesomeIcon icon={faPhone} className="me-1" style={{ color: isDark ? '#94a3b8' : '#6c757d' }} />
                          {c.vTelefonoPersonal || c.vTelefonoTrabajo || 'Sin teléfono'}
                        </span>
                        {c.idLicencia && (
                          <span
                            className="badge flex-shrink-0"
                            style={{
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              backgroundColor: isDark ? 'rgba(56, 189, 248, 0.15)' : '#e0f2fe',
                              color: isDark ? '#38bdf8' : '#0284c7',
                              border: isDark ? '1px solid rgba(56, 189, 248, 0.3)' : '1px solid #bae6fd',
                              borderRadius: '4px',
                              padding: '1px 5px'
                            }}
                            title={`Licencia #${c.idLicencia}`}
                          >
                            Licencia #{c.idLicencia}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-1 flex-shrink-0">
                      {isSelected && (
                        <span className="badge bg-success" style={{ fontSize: '0.65rem' }}>
                          <FontAwesomeIcon icon={faCheck} />
                        </span>
                      )}
                      <a
                        href={`/clientes/detalle/${c.idClienteAkasia}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-link p-0 d-inline-flex align-items-center justify-content-center text-decoration-none"
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '4px',
                          color: isDark ? '#38bdf8' : '#2563eb',
                          backgroundColor: isDark ? 'rgba(56, 189, 248, 0.12)' : 'rgba(37, 99, 235, 0.08)',
                          border: isDark ? '1px solid rgba(56, 189, 248, 0.25)' : '1px solid rgba(37, 99, 235, 0.2)'
                        }}
                        title={`Abrir cliente #${c.idClienteAkasia} en otra pestaña`}
                        onClick={e => e.stopPropagation()}
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.70rem' }} />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}

            {isLoadingMore && (
              <div className="text-center py-2" style={{ fontSize: '0.70rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                <FontAwesomeIcon icon={faSpinner} spin className="me-1 text-success" />
                Cargando más clientes...
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AssignClientDropdown;
